<template>
  <v-row align="start">
    <v-col class="pr-0">
      <vs-autocomplete
        ref="itemNameAutoCompleteRef"
        v-model.number="model.assetConditionId"
        label="Item name"
        hide-mini-label
        :disabled="!isCreatingNewInventory"
        :items="selectableSubAssets"
        required
      />
    </v-col>
    <v-col class="ml-2 px-0" cols="2">
      <vs-text-input
        :value="model.quantityChange"
        label="Qty"
        type="number"
        item-value="text"
        free-form
        required
        @input="handleChangeQty"
      />
    </v-col>
    <v-col v-if="!isCreatingNewInventory" class="ml-2 px-0" cols="2">
      <vs-text-input
        :value="quantity"
        label="Stock"
        type="number"
        item-value="text"
        free-form
        required
        @input="handleChangeStock"
      />
    </v-col>

    <v-col v-else class="shrink py-5 px-0 ml-2">
      <vs-button icon="close" small destructive type="tertiary" @click="handleRemove" />
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { orderBy } from 'lodash'
import { computed, ref, toRef } from 'vue'
import VsAutocomplete from '@/components/vision/Inputs/VsAutocomplete/VsAutocomplete.vue'
import { AssetQuantityCondition } from '@/models/asset'
import { InventoryQuantityChangeModel } from '@/models/inventory_adjustment'
import Big from 'big.js'

const props = defineProps<{
  allSubAssets: AssetQuantityCondition[]
  takenSubAssetsIds: number[]
  initialQuantity: number
  index: number
  modelProps: InventoryQuantityChangeModel
}>()

const model = toRef(props, 'modelProps')

const isCreatingNewInventory = props.initialQuantity === 0

const selectableSubAssets = computed(() => {
  const subAssets = props.allSubAssets.filter(
    (sa) => sa.id === model.value.assetConditionId || !props.takenSubAssetsIds.includes(sa.id ?? 0),
  )
  return orderBy(subAssets, [(asset) => asset.name.toLowerCase()]).map((asset) => {
    return { text: asset.name, value: asset.id }
  })
})

const quantity = computed(() => {
  const quantityChangePrecise = new Big(model.value.quantityChange ?? 0)
  const initialQuantityPrecise = new Big(props.initialQuantity)
  return quantityChangePrecise.plus(initialQuantityPrecise).toNumber()
})

const emit = defineEmits<{
  (e: 'on-remove', index: number)
  (e: 'on-change-quantity', value: number)
}>()

const handleRemove = () => {
  emit('on-remove', props.index)
}

const isNegativeZero = (num: string) => {
  // Check if num is of the form '-0.0000000...00'
  // '-0' returns true, '-0.' returns true, '-0.00...0' returns true
  if (num.length === 0) return false
  if (num[0] !== '-') {
    return false
  }
  let numDecimalPoint = 0
  let numNegativeSign = 0
  let numZero = 0
  for (let i = 0; i < num.length; i++) {
    if (num[i] !== '-' && num[i] !== '0' && num[i] !== '.') {
      return false
    }
    if (num[i] === '-') numNegativeSign++
    if (num[i] === '.') numDecimalPoint++
    if (num[i] === '0') numZero++
  }
  return !(numDecimalPoint > 1 || numNegativeSign !== 1 || numZero === 0)
}

const handleChangeQty = (value: string) => {
  if (!value || isNegativeZero(value)) {
    // We want to type the number of type "-0.xyz...", e.g., "-0.1", "-0.0002", ...
    // If this "if" statement doesn't exist, then when we type "-0", we have num = parseFloat("-0") = 0
    // Therefore, we lost the negative sign.
    // Similarly, when we type "-" only, event === "" (empty string), so parseFloat(event) will return NaN
    return
  }
  model.value.quantityChange = parseFloat(value)
}
const handleChangeStock = (value: string) => {
  if (!value || isNegativeZero(value)) {
    // We want to type the number of type "-0.xyz...", e.g., "0.1", "0.0002", ...
    // If this "if" statement doesn't exist, then when we type "-0", we have num = parseFloat("-0") = 0
    // Therefore, we lost the negative sign.
    // Similarly, when we type "-" only, event === "" (empty string), so parseFloat(event) will return NaN
    return
  }
  emit('on-change-quantity', parseFloat(value))
}

const itemNameAutoCompleteRef = ref<VsAutocomplete>()

const focusAssetConditionId = () => {
  itemNameAutoCompleteRef.value?.focus()
}

defineExpose({ focusAssetConditionId })
</script>
